"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.hasUsablePlaceStats = hasUsablePlaceStats;
exports.predictNextPlaceId = predictNextPlaceId;
exports.rankPlaceChips = rankPlaceChips;
exports.softReweightDefects = softReweightDefects;
const place_stats_1 = require("./place-stats");
const place_stats_rooms_1 = require("./place-stats-rooms");
function logProb(share) {
    return Math.log(Math.max(share, place_stats_1.PLACE_STATS_SMOOTH));
}
function shareMap(shares) {
    return new Map((shares ?? []).map((share) => [share.placeId, share.share]));
}
function resolveDefectPlaces(stats, defectId, categoryId, attached) {
    const own = (defectId && stats?.defectStats[defectId]) || attached;
    if (own && own.n >= place_stats_1.PLACE_STATS_MIN_SUPPORT)
        return shareMap(own.places);
    const category = categoryId ? stats?.categoryStats[categoryId] : undefined;
    if (category && category.n >= place_stats_1.PLACE_STATS_MIN_SUPPORT)
        return shareMap(category.places);
    return shareMap(stats?.globalPlacePrior);
}
function orderShares(stats, previousPlaceId) {
    if (!stats)
        return new Map();
    if (previousPlaceId && stats.orderPrior.source === 'learned') {
        const next = stats.orderPrior.transitions[previousPlaceId];
        if (next?.length)
            return shareMap(next);
    }
    if (!previousPlaceId && stats.orderPrior.firstPlaceShares.length) {
        return shareMap(stats.orderPrior.firstPlaceShares);
    }
    return shareMap(stats.globalPlacePrior);
}
function catalogById(places) {
    return new Map(places.filter((place) => place.id).map((place) => [place.id, place]));
}
function hasUsablePlaceStats(stats) {
    return Boolean(stats && (stats.nReports > 0 || Object.keys(stats.defectStats).length));
}
function predictNextPlaceId(input) {
    return rankPlaceChips({
        catalogPlaces: input.catalogPlaces,
        previousPlaceId: input.previousPlaceId,
        stats: input.stats,
        limit: 1,
    })[0]?.id;
}
function rankPlaceChips(input) {
    const limit = input.limit ?? place_stats_1.PLACE_CHIP_LIMIT;
    const selected = new Set((input.selectedPlaceIds ?? []).map(String));
    const catalog = catalogById(input.catalogPlaces);
    for (const place of input.aiPlaces ?? []) {
        if (place.id && !catalog.has(place.id))
            catalog.set(place.id, place);
    }
    const available = [...catalog.values()].filter((place) => !selected.has(place.id));
    if (!available.length)
        return [];
    if (!hasUsablePlaceStats(input.stats)) {
        const seen = new Set(selected);
        const out = [];
        const push = (place) => {
            if (!place?.id || seen.has(place.id))
                return;
            seen.add(place.id);
            out.push(place);
        };
        push(input.previousPlaceId ? catalog.get(input.previousPlaceId) : undefined);
        for (const place of input.neighborPlaces ?? [])
            push(place);
        for (const place of input.aiPlaces ?? [])
            push(place);
        return out.slice(0, limit);
    }
    const defectGiven = resolveDefectPlaces(input.stats, input.selectedDefectId, input.selectedDefectCategoryId, input.selectedDefectStats);
    const orderGiven = orderShares(input.stats, input.previousPlaceId);
    const aiIds = new Set((input.aiPlaces ?? []).map((place) => place.id).filter(Boolean));
    const hasSelectedDefect = Boolean(input.selectedDefectId || input.selectedDefectStats);
    const scored = available
        .filter((place) => place.id !== input.previousPlaceId)
        .map((place) => ({
        place,
        score: (hasSelectedDefect ? logProb(defectGiven.get(place.id) ?? 0) : 0) +
            logProb(orderGiven.get(place.id) ?? 0) +
            (aiIds.has(place.id)
                && (!hasSelectedDefect || (defectGiven.get(place.id) ?? 0) >= place_stats_1.PLACE_STATS_MIN_PLACE_SHARE)
                ? place_stats_1.PLACE_STATS_AI_LOG_BOOST
                : 0),
    }))
        .sort((left, right) => {
        if (right.score !== left.score)
            return right.score - left.score;
        return (0, place_stats_rooms_1.compareCanonicalPlaces)(left.place, right.place);
    })
        .map((entry) => entry.place);
    if (input.stats?.orderPrior.source === 'canonical' && !input.selectedDefectId && !input.selectedDefectStats) {
        scored.sort(place_stats_rooms_1.compareCanonicalPlaces);
    }
    const previous = input.previousPlaceId ? catalog.get(input.previousPlaceId) : undefined;
    const head = previous && !selected.has(previous.id) ? [previous] : [];
    const rest = scored.filter((place) => place.id !== previous?.id);
    const room = Math.max(0, limit - head.length);
    let visible = rest.slice(0, room);
    const topAi = (input.aiPlaces ?? []).find((place) => place.id && !selected.has(place.id) && place.id !== previous?.id);
    const reservedAi = topAi && place_stats_1.PLACE_STATS_RESERVED_AI_SLOTS > 0
        ? catalog.get(topAi.id) ?? topAi
        : undefined;
    if (reservedAi
        && !head.some((place) => place.id === reservedAi.id)
        && !visible.some((place) => place.id === reservedAi.id)) {
        if (room > 0 && visible.length >= room) {
            visible = [...visible.slice(0, room - 1), reservedAi];
        }
        else if (head.length + visible.length < limit) {
            visible.push(reservedAi);
        }
    }
    return [...head, ...visible].slice(0, limit);
}
function softReweightDefects(input) {
    const targetPlaceId = (input.tappedPlaceIds ?? []).find(Boolean);
    if (!targetPlaceId || !hasUsablePlaceStats(input.stats))
        return input.defects;
    return [...input.defects]
        .map((defect, index) => {
        const given = resolveDefectPlaces(input.stats, defect.id, defect.categoryId);
        const placeWeight = given.get(targetPlaceId) ?? 0;
        const base = defect.score ?? input.defects.length - index;
        return { defect, score: logProb(base) + logProb(placeWeight) };
    })
        .sort((left, right) => right.score - left.score)
        .map((entry) => entry.defect);
}
